import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../../styles/theme';
import { LIVE_BOAT_FLOW_EDGES } from '../../config/liveBoatRoles';
import { edgePath, parseW, LIVE_BOAT_NODE_LAYOUT } from '../../utils/liveBoatUtils';

const flowAnim = keyframes`
  to { stroke-dashoffset: -20; }
`;

const Wrap = styled.div`
  position: relative;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  height: ${(p) => (p.$compact ? '220px' : '280px')};
  overflow: hidden;
`;

const FlowSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const FlowPath = styled.path`
  fill: none;
  stroke: ${(p) => (p.$active ? theme.colors.secondary.light : theme.colors.border.primary)};
  stroke-width: ${(p) => (p.$active ? 2 : 1.25)};
  stroke-dasharray: ${(p) => (p.$active ? '6 4' : 'none')};
  animation: ${(p) => (p.$active ? flowAnim : 'none')} 1.1s linear infinite;
  animation-direction: ${(p) => (p.$reverse ? 'reverse' : 'normal')};
  opacity: ${(p) => (p.$active ? 1 : 0.5)};
`;

const Node = styled.div`
  position: absolute;
  left: ${(p) => p.$x}%;
  top: ${(p) => p.$y}%;
  transform: translate(-50%, -50%);
  text-align: center;
  max-width: 88px;
  padding: 0.35rem 0.45rem;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.75);
  border: 1px solid ${theme.colors.border.secondary};
`;

const NodeLabel = styled.div`
  font-size: 0.58rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.12rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NodeValue = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  line-height: 1.2;
`;

const FLOW_NODES = ['shore', 'solar', 'houseBattery', 'acLoads', 'dcLoads'];

const ElectricalFlowDiagram = ({ roles, heroForRole, compact = false }) => {
  const edges = useMemo(() => {
    return LIVE_BOAT_FLOW_EDGES.map((edge) => {
      const fromRole = roles[edge.from];
      const powerField = fromRole?.fields?.[edge.powerKey];
      const watts = Math.abs(parseW(powerField));
      const raw = parseW(powerField);
      return {
        ...edge,
        active: watts >= 3,
        reverse: raw < 0,
        pathD: edgePath(LIVE_BOAT_NODE_LAYOUT[edge.from], LIVE_BOAT_NODE_LAYOUT[edge.to]),
      };
    });
  }, [roles]);

  return (
    <Wrap $compact={compact}>
      <FlowSvg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        {edges.map((e) => (
          <FlowPath
            key={e.label}
            d={e.pathD}
            $active={e.active}
            $reverse={e.reverse}
          />
        ))}
      </FlowSvg>
      {FLOW_NODES.map((roleId) => {
        const layout = LIVE_BOAT_NODE_LAYOUT[roleId];
        const role = roles[roleId];
        if (!layout || !role) return null;
        return (
          <Node key={roleId} $x={layout.x} $y={layout.y}>
            <NodeLabel>{role.label}</NodeLabel>
            <NodeValue>{heroForRole(roleId, role)}</NodeValue>
          </Node>
        );
      })}
    </Wrap>
  );
};

export default ElectricalFlowDiagram;
